<template>
    <div ref="articleContainer">
        <div
            v-if="article && !loading"
            class="w-full flex flex-col bg-white p-8 box-border"
        >
            <router-link
                to="/public-news"
                class="self-start text-body-6 p-4 rounded mb-4 bg-gray-300 text-black"
            >
                Back
            </router-link>
            <Image
                v-if="hasCover"
                :src="article.cover"
                type="cover"
            />
            <h2 :class="{'mt-4': hasCover}" class="xxs:text-xl lg:text-2xl">{{ article.title }}</h2>
            <div
                class="w-full mt-4 text-default-1"
                v-html="article.body"
            >
            </div>
        </div>
        <div v-if="!article && loading" class="w-full flex flex-col bg-white p-8 box-border h-lvh">
            <div class="w-[80%] h-10 skeleton-loader mt-4"></div>
            <div class="mt-4 w-full">
                <div class="w-full h-5 skeleton-loader mt-1" v-for="i in 10" :key="i"></div>
            </div>
        </div>
        <hr class="w-[90%] last:hidden" />
    </div>
</template>
<script setup>
import {onBeforeMount, onMounted, onBeforeUnmount, ref} from "vue";
import {useNews} from "@/composables/useNews";
import {useRoute, useRouter} from "vue-router";
import {computed, nextTick} from "vue";
import {useAuth} from "@/stores/AuthStore";
import Image from "@/components/custom/Image.vue";

const { organization } = useAuth();
const { getPublicArticle } = useNews();
const route = useRoute();
const router = useRouter();

const article = ref(null);
const loading = ref(false);

const articleContainer = ref(null);
const isContentLoaded = ref(false);
const videosCount = ref(0);

const hasCover = computed(() => {
    if(!article.value || article.value.cover === null) {
        return false;
    }

    return article.value.cover.length > 0;
})

function sendHeight() {
    const height = document.body.scrollHeight + videosCount.value * 300;
    window.parent.postMessage({ height }, '*');
}

function onContentLoaded() {
    isContentLoaded.value = true;
    sendHeight();
}

function monitorContentLoading() {
    isContentLoaded.value = false;
    const container = articleContainer.value;
    if (!container) return;

    const mediaElements = container.querySelectorAll("img, video, iframe");
    let loadedCount = 0;

    function checkAllLoaded() {
        loadedCount += 1;
        if (loadedCount === mediaElements.length) {
            onContentLoaded();
        }
    }

    if (mediaElements.length === 0) {
        onContentLoaded();
    } else {
        mediaElements.forEach((media) => {
            if(media.src.includes('youtube') && !media.classList.contains("youtube-video")) {
                media.classList.add("youtube-video");
                videosCount.value += 1;
            }
            media.addEventListener("load", checkAllLoaded, { once: true });
            media.addEventListener("error", checkAllLoaded, { once: true });
        });
    }
}

function observeContentChanges() {
    const observer = new MutationObserver(async () => {
        await nextTick();
        monitorContentLoading();
    });

    observer.observe(articleContainer.value, {
        childList: true,
        subtree: true,
    });

    return observer;
}

onBeforeMount(() => {
    loading.value = true;

    if(route.params.uid) {
        getPublicArticle(organization.slug, route.params.uid).then((response) => {
            article.value = response;
        }).catch((error) => {
            console.log(error);
            router.push({name: 'PublicNews'});
        }).finally(() => {
            loading.value = false;
        });
    }
})

onMounted(async () => {
    await nextTick();
    const observer = observeContentChanges();
    monitorContentLoading();
    window.addEventListener("resize", sendHeight);

    onBeforeUnmount(() => {
        observer.disconnect();
        window.removeEventListener("resize", sendHeight);
    });
});
</script>
<style  lang="scss" scoped>
    .youtube-video {
        @media (max-width: 768px) {
            width: fit-content;
        }
    }

.article-body {
  iframe{
    @media(max-width: 768px){
      max-width: 100%;
    }
  }
}

ol, ul {
  list-style: disc;
  margin-block-start: 1em;
  margin-block-end: 1em;
  padding-inline-start: 40px;
}

h1 {
  font-size: 2.125rem;
}

h2 {
  font-size: 1.875rem;
}

h3 {
  font-size: 1.5rem;
}

h4 {
  font-size: 1.25rem;
}

h5 {
  font-size: 1.125rem;
}

h6 {
  font-size: 1rem;
}
</style>
